import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Brand, LanguageToggle } from "../components/Brand";

export default function ForcePassword() {
  const { t } = useI18n();
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setOk(true);
      await refresh();
      setTimeout(() => {
        if (user?.role === "super_admin") navigate("/super-admin");
        else if (user?.role === "tenant_admin") navigate("/admin");
        else navigate("/staff");
      }, 800);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
        <div className="flex items-center justify-between">
          <Brand />
          <LanguageToggle />
        </div>
        <div className="mt-6 flex items-center gap-2 text-blue-800 bg-blue-50 border border-blue-100 rounded-xl px-3 py-2 text-sm">
          <Lock className="w-4 h-4" /> {t("change_password_required")}
        </div>
        {user?.is_demo && (
          <div className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
            {t("demo_readonly_notice")}
          </div>
        )}
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="force-pw-form">
          <input
            type="password"
            required
            placeholder={t("current_password")}
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            data-testid="force-pw-current"
            className="w-full px-3 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
          />
          <input
            type="password"
            required
            minLength={8}
            placeholder={t("new_password")}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            data-testid="force-pw-new"
            className="w-full px-3 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
          />
          {error && <div className="text-sm text-rose-600" data-testid="force-pw-error">{error}</div>}
          {ok && <div className="text-sm text-emerald-600">{t("password_updated")}</div>}
          <button
            type="submit"
            disabled={loading}
            data-testid="force-pw-submit"
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-700 text-white font-medium hover:bg-blue-800 disabled:opacity-60"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {t("update_password")}
          </button>
          <button type="button" onClick={() => { logout(); navigate("/login"); }} className="w-full text-sm text-slate-500 hover:text-slate-800">
            {t("logout")}
          </button>
        </form>
      </div>
    </div>
  );
}
